import React from 'react';

import { Checkbox, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { Box } from '@mui/system';
import { DateTime } from 'luxon';

import { ReactComponent as DiscountIcon } from '../../../../../../assets/svg/discountIcon.svg';
import { getHoursDiff } from '../../../../../Modals/WorkshiftDetails/utils';
import { TextFieldVariants } from '../../../../../../helpers/Constants';
import CustomCheckbox from '../../../../../common/CustomCheckbox';
import DateTimeRange from '../../../../../common/DateTimeRange';
import strings from '../../../../../../localization';

import styles from './styles';

interface IDateBox {
  title: string;
  formik: FormikProps<any>;
  startName: string;
  endName: string;
  dayOffName: string;
  changedName: string;
  hideDayOff?: boolean;
  handleCheckboxClick?: () => void;
  checkboxValue?: boolean;
  disabled?: boolean;
  showCheckbox?: boolean;
  setDisabled?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  Chip?: React.ReactNode;
  textColor?: string | null;
  isPromoBooking?: boolean;
  disabledObject?: Record<string, boolean>;
}

const handleDate = (date: string | Date) => {
  if (date instanceof Date) {
    return DateTime.fromJSDate(date).toISO({ includeOffset: false });
  }

  return DateTime.fromISO(date).toISO({ includeOffset: false });
};

const DateBox: React.FC<IDateBox> = ({
  title,
  formik,
  startName,
  endName,
  dayOffName,
  changedName,
  hideDayOff = false,
  disabled,
  handleCheckboxClick,
  checkboxValue,
  Chip,
  showCheckbox,
  setDisabled,
  textColor,
  disabledObject,
  isPromoBooking,
}) => {
  const startHandler = () => {
    formik.setFieldValue(endName, null);
    formik.setFieldValue(changedName, true);

    setDisabled?.({ ...disabledObject, [startName]: true });
  };

  const endHandler = () => {
    formik.setFieldValue(changedName, true);
    setDisabled?.({ ...disabledObject, [startName]: false });
  };

  const difference = getHoursDiff(
    handleDate(formik.values?.[startName] ?? '') ?? '',
    handleDate(formik.values?.[endName] ?? '') ?? '',
  );

  const handleChange = () => {
    formik.setFieldValue(changedName, true);
    formik.setFieldValue(dayOffName, !formik.values[dayOffName]);
  };

  return (
    <Box sx={styles.dateBoxContainer}>
      <Box sx={styles.contractTitleContainer}>
        <Box sx={styles.titleContainer}>
          {isPromoBooking && (
            <Box mr="8px" sx={{ display: 'flex', alignItems: 'center' }}>
              <DiscountIcon />
            </Box>
          )}
          {showCheckbox && (
            <Checkbox
              sx={styles.requestCheckbox}
              checked={checkboxValue}
              onClick={handleCheckboxClick}
            />
          )}
          <Typography sx={styles.contractTitle}>{title}</Typography>
        </Box>
        {Chip}
        {!hideDayOff && (
          <CustomCheckbox
            labelClass={styles.labelClass}
            customClass={styles.customCheckboxClass}
            customCheckboxStyle={{ padding: 0 }}
            label={strings.dayOff}
            name={dayOffName}
            handleChange={handleChange}
            value={formik.values?.[dayOffName] ?? false}
            id=""
          />
        )}
      </Box>
      <Box sx={styles.timeContainer}>
        <DateTimeRange
          textColor={textColor}
          disabled={disabled || !!formik.values?.[dayOffName]}
          formik={formik}
          startName={startName}
          endName={endName}
          customStartHandler={startHandler}
          customEndHandler={endHandler}
          allowValidOnly={false}
          startPlaceholder="00:00"
          variant={TextFieldVariants.filled}
          endPlaceholder="00:00"
          horizontal={false}
          removeMargin
        />
        <Typography sx={styles.subTitle}>
          {`(${difference ?? 0}${strings.hoursAbbreviation})`}
        </Typography>
      </Box>
    </Box>
  );
};

export default DateBox;
